/**
 * Users — account search + detail with the SM-11 tooling: restrict a surface,
 * lift restrictions, ban / ban+device / unban, EC-28 device-hash lifts. Shows
 * exactly the fields the endpoints already expose — no new PII surfaces.
 */
import { useState } from "react";
import { copy } from "@humble/shared";
import { Button, Sheet, toast } from "../../design/index.js";
import { isApiError } from "../../lib/api.js";
import { rawApi } from "../safety/raw-api.js";

const SURFACES = ["roulette", "media_upload", "super_reject", "deck", "calls", "purchase"] as const;

interface UserRow {
  id: string;
  email: string | null;
  display_name: string | null;
  lifecycle: string;
  return_state: string | null;
  role: string;
  verification: string;
  banned_at: string | null;
  created_at: string;
}

interface UserDetail {
  user: UserRow & {
    grace_due_at: string | null;
    discoverable: boolean;
    live_photo_count: number;
    last_active_at: string | null;
  };
  restrictions: Array<{
    id: string;
    surface: string;
    until_at: string;
    active: boolean;
    reason_ref: string | null;
    created_at: string;
  }>;
  sr_history: Array<{ id: string; target_id: string; state: string; fired_at: string }>;
  reports_against: Array<{ id: string; category: string; status: string; created_at: string }>;
  strike_count_90d: number;
  device_hashes: Array<{ device_fp_hash: string; denylisted: boolean }>;
}

function Field({ label, value }: { label: string; value: string | number | boolean | null }) {
  return (
    <div className="admin__field-row">
      <span className="admin__field-label">{label}</span>
      <span className="admin__field-value">
        {value === null || value === "" ? "—" : typeof value === "boolean" ? (value ? "yes" : "no") : String(value)}
      </span>
    </div>
  );
}

export function UsersSection() {
  const [q, setQ] = useState("");
  const [searched, setSearched] = useState(false);
  const [results, setResults] = useState<UserRow[]>([]);
  const [detail, setDetail] = useState<UserDetail | null>(null);
  const [busy, setBusy] = useState(false);
  const [restrictSurface, setRestrictSurface] = useState<(typeof SURFACES)[number]>("roulette");
  const [restrictDays, setRestrictDays] = useState(7);

  const search = async () => {
    if (!q.trim()) return;
    try {
      setResults(await rawApi("GET", `/admin/users?q=${encodeURIComponent(q.trim())}`));
      setSearched(true);
    } catch (err) {
      toast(isApiError(err) ? err.message : "Search failed", "error");
    }
  };

  const openUser = async (id: string) => {
    try {
      setDetail(await rawApi<UserDetail>("GET", `/admin/users/${id}`));
    } catch (err) {
      toast(isApiError(err) ? err.message : copy.admin.load_failed, "error");
    }
  };

  const userId = detail?.user.id;

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      if (userId) await openUser(userId);
    } catch (err) {
      toast(isApiError(err) ? err.message : copy.admin.action_failed, "error");
    } finally {
      setBusy(false);
    }
  };

  const restrict = () =>
    run(() =>
      rawApi("POST", `/admin/users/${userId}/restrict`, {
        surface: restrictSurface,
        until: new Date(Date.now() + restrictDays * 86_400_000).toISOString(),
      }),
    );

  return (
    <div data-testid="admin-users">
      <div className="admin__search">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") void search();
          }}
          placeholder={copy.admin.users.search_placeholder}
          aria-label="Search users"
        />
        <Button variant="primary" onClick={() => void search()}>
          Search
        </Button>
      </div>

      {results.length > 0 && (
        <div className="admin__tablewrap">
          <table className="admin__table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Lifecycle</th>
                <th>Role</th>
                <th>Verified</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {results.map((u) => (
                <tr key={u.id}>
                  <td>
                    <strong>{u.display_name ?? "—"}</strong>
                  </td>
                  <td className="admin__meta">{u.email ?? "—"}</td>
                  <td>
                    <span className={`admin__pill admin__pill--lc-${u.lifecycle}`}>{u.lifecycle}</span>
                  </td>
                  <td>{u.role}</td>
                  <td>{u.verification}</td>
                  <td className="admin__time">{new Date(u.created_at).toLocaleDateString()}</td>
                  <td>
                    <Button size="sm" onClick={() => void openUser(u.id)}>
                      Open
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {searched && results.length === 0 && <p className="admin__empty">{copy.admin.users.no_results}</p>}

      <Sheet open={detail !== null} onClose={() => setDetail(null)} title="User">
        {detail && userId && (
          <div className="admin__detail">
            <div className="admin__field-grid">
              <Field label="Display name" value={detail.user.display_name} />
              <Field label="Email" value={detail.user.email} />
              <Field label="ID" value={detail.user.id} />
              <Field label="Lifecycle" value={detail.user.lifecycle} />
              <Field label="Role" value={detail.user.role} />
              <Field label="Verification" value={detail.user.verification} />
              <Field label="Discoverable" value={detail.user.discoverable} />
              <Field label="Live photos" value={detail.user.live_photo_count} />
              <Field
                label="Created"
                value={detail.user.created_at ? new Date(detail.user.created_at).toLocaleString() : null}
              />
              <Field
                label="Last active"
                value={detail.user.last_active_at ? new Date(detail.user.last_active_at).toLocaleString() : null}
              />
              <Field
                label="Banned at"
                value={detail.user.banned_at ? new Date(detail.user.banned_at).toLocaleString() : null}
              />
              <Field
                label="Grace due"
                value={detail.user.grace_due_at ? new Date(detail.user.grace_due_at).toLocaleString() : null}
              />
            </div>
            <p className="admin__meta" style={{ margin: 0 }}>
              Strikes (90 d): {detail.strike_count_90d} · Reports against: {detail.reports_against.length} · SRs
              fired: {detail.sr_history.length}
            </p>

            <h3 className="admin__panel-title">Restrictions</h3>
            {detail.restrictions.length === 0 && <p className="admin__meta">None.</p>}
            {detail.restrictions.map((r) => (
              <div key={r.id} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <span style={{ flex: 1 }}>
                  {r.surface} until {new Date(r.until_at).toLocaleString()}{" "}
                  {r.active ? "(active)" : "(lifted/expired)"}
                </span>
                {r.active && (
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={busy}
                    onClick={() => void run(() => rawApi("DELETE", `/admin/users/${userId}/restrictions/${r.id}`))}
                  >
                    Lift
                  </Button>
                )}
              </div>
            ))}
            <div className="admin__restrict">
              <span className="admin__meta">{copy.admin.users.restrict}:</span>
              <select value={restrictSurface} onChange={(e) => setRestrictSurface(e.target.value as never)}>
                {SURFACES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
              <input
                type="number"
                min={1}
                max={365}
                value={restrictDays}
                onChange={(e) => setRestrictDays(Number(e.target.value))}
                style={{ width: 70 }}
                aria-label="Restriction days"
              />
              <span className="text-dim">days</span>
              <Button size="sm" variant="danger" loading={busy} onClick={() => void restrict()}>
                {copy.admin.users.restrict_apply}
              </Button>
            </div>

            <h3 className="admin__panel-title">Reports against</h3>
            {detail.reports_against.length === 0 && <p className="admin__meta">None.</p>}
            {detail.reports_against.slice(0, 10).map((r) => (
              <div key={r.id} className="admin__rowhead">
                <strong>{r.category}</strong>
                <span className={`admin__pill admin__pill--${r.status}`}>{r.status}</span>
                <span className="admin__time">{new Date(r.created_at).toLocaleString()}</span>
              </div>
            ))}

            <h3 className="admin__panel-title">Device hashes</h3>
            {detail.device_hashes.length === 0 && <p className="admin__meta">None seen.</p>}
            {detail.device_hashes.map((d) => (
              <div key={d.device_fp_hash} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <code className="admin__hash">{d.device_fp_hash}</code>
                {d.denylisted ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={busy}
                    onClick={() => void run(() => rawApi("DELETE", `/admin/device-denylist/${d.device_fp_hash}`))}
                  >
                    Lift (EC-28)
                  </Button>
                ) : (
                  <span className="text-faint">clear</span>
                )}
              </div>
            ))}

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Button
                variant="danger"
                loading={busy}
                onClick={() => void run(() => rawApi("POST", `/admin/users/${userId}/ban`, { device_ban: false }))}
              >
                Ban account
              </Button>
              <Button
                variant="danger"
                loading={busy}
                onClick={() => void run(() => rawApi("POST", `/admin/users/${userId}/ban`, { device_ban: true }))}
              >
                Ban account + device
              </Button>
              <Button
                variant="primary"
                loading={busy}
                onClick={() => void run(() => rawApi("POST", `/admin/users/${userId}/unban`))}
              >
                Unban (appeal granted)
              </Button>
            </div>

            <details>
              <summary className="admin__meta">Raw record</summary>
              <pre className="admin__json">{JSON.stringify(detail.user, null, 2)}</pre>
            </details>
          </div>
        )}
      </Sheet>
    </div>
  );
}
